import { valueOrDefault, valueOrThrow } from "system/Utilities";
import APIRequest, { APIError } from "./APIRequest";  // Used to perform a login

export enum UserPreference {
    AVATAR = "avatar",
    FAVORITE_QUERIES = "favorite_queries",
    LOCALE = "locale",
    THEME = "theme",
    TIME_ZONE = "time_zone",

    DEFAULT_QUICKSEARCH_VS_AI = "default_quicksearch_vs_ai",
    SKIP_CONVERTING_QUICKSEARCH_BOOLEAN = "skip_converting_quicksearch_boolean",
    DEFAULT_HOMEPAGE = "default_homepage"
}

export enum SeatTypes {
  ANALYST = "analyst",
  API_ONLY = "api-only",
  QUICK_SEARCH = "quick-search",
  VIEW_ONLY = "view-only",
  PULSE_AI = "ai-studio"
}

export const SeatTypeMap: Record<SeatTypes, string> = {
  [SeatTypes.ANALYST]: "user-account-info.seat-types.analyst",
  [SeatTypes.API_ONLY]: "user-account-info.seat-types.api-only",
  [SeatTypes.QUICK_SEARCH]: "user-account-info.seat-types.quick_search",
  [SeatTypes.VIEW_ONLY]: "user-account-info.seat-types.view_only",
  [SeatTypes.PULSE_AI]: "user-account-info.pulse_ai",
};

export enum DataPlans {
  CORE = "core",
  ENTERPRISE = "enterprise",
  PRO = "pro",
  PREMIUM_EMERGING = "premium+emerging",
  PULSE_AI = "pulse_ai"
}

export const DataPlanMap: Record<DataPlans, string> = {
  [DataPlans.CORE]: "user-account-info.data-plans.core",
  [DataPlans.ENTERPRISE]: "user-account-info.data-plans.enterprise",
  [DataPlans.PRO]: "user-account-info.data-plans.pro",
  [DataPlans.PREMIUM_EMERGING]: "user-account-info.data-plans.premium+emerging",
  [DataPlans.PULSE_AI]: "user-account-info.pulse_ai"
};

export class InfegyAccount {
    readonly id: string;
    readonly created_at: Date = new Date();
    readonly updated_at: Date = new Date();
    readonly name: string = "";
    readonly domain?: string;
    readonly plan: string = "core";
    readonly social_plan?: string;
    readonly addon_plans: Array<string> = [];
    readonly billing_order_id?: string | null = null;
    readonly trial_order_id?: string | null = null;
    readonly subscription_management_link?: string;
    readonly api_payg_billed_to?: string | null = null;
      
    constructor(_json: any) {
      this.id = valueOrThrow(_json, "id", "string");
      this.created_at = valueOrThrow(_json, "created_at", "Date");
      this.updated_at = valueOrThrow(_json, "updated_at", "Date");
      this.name = valueOrThrow(_json, "name", "string");
      this.domain = valueOrDefault(_json, "domain", "string", undefined);
      this.plan = valueOrThrow(_json, "plan", "string");
      this.social_plan = valueOrDefault(_json, "social_plan", "string", undefined);
      this.addon_plans = valueOrDefault(_json, "addon_plans", "object", []);
      this.billing_order_id = valueOrDefault(_json, "billing_order_id", "string", null);
      this.trial_order_id = valueOrDefault(_json, "trial_order_id", "string", null);
      this.subscription_management_link = valueOrDefault(_json, "subscription_management_link", "string", undefined);
      this.api_payg_billed_to = valueOrDefault(_json, "api_payg_billed_to", "string", null)
    }
}

export class InfegyUser {
    readonly id: string;
    readonly created_at: Date = new Date();
    readonly updated_at: Date = new Date();
    readonly account_id: string;
    readonly email: string = "";
    readonly name: string = "";
    second_factor: string = "none";
    readonly intercom_user: string = "";
    readonly role: string = "";
    readonly admin: boolean = false;
    readonly account: InfegyAccount;

    readonly timezone: string = "UTC/UTC";

    readonly permissions: string[] = [];
    preferences: any = {};

    constructor(_json: any) {
        this.id = valueOrThrow(_json, "id", "string");
        this.created_at = valueOrThrow(_json, "created_at", "Date");
        this.updated_at = valueOrThrow(_json, "updated_at", "Date");
        this.account_id = valueOrThrow(_json.account, "id", "string");
        this.email = valueOrThrow(_json, "email", "string");
        this.name = valueOrThrow(_json, "name", "string");
        this.second_factor = valueOrThrow(_json, "second_factor", "string");
        this.intercom_user = valueOrDefault(_json, "intercom_user", "string", "");
        this.role = valueOrThrow(_json, "role", "string");
        this.admin = valueOrDefault(_json, "admin", "boolean", false);
        this.account = new InfegyAccount(_json["account"]);

        this.timezone = valueOrDefault(_json, "timezone", "string", "UTC/UTC");

        this.permissions = valueOrThrow(_json, "permissions", "object");
        this.preferences = valueOrThrow(_json, "preferences", "object");
    }

    getInitials(): string {
        return getInitials(this.name);
    }
    getPreference(preference: UserPreference): string {
        switch (preference) {
            case UserPreference.LOCALE:
                try {
                    // Typescript Error: Property 'getCanonicalLocales' does not exist on type 'typeof Intl'.ts(2339)
                    // This was fixed 2 weeks ago (2023 Nov 3) but hasn't been included in a new release version yet
                    // See the PR for details: https://github.com/microsoft/TypeScript/pull/56079
                    const locale = Intl.getCanonicalLocales([this.preferences.locale])[0];
                    return locale;
                } catch {
                    return navigator.language;
                }
            default:
                return this.preferences[preference] || "";
        }
    }

    setPreference(preference: UserPreference, value: string): void {
        if (value) {
            this.preferences[preference] = value;
        } else {
            delete this.preferences[preference];
        }
        
    }

    async savePreferences() {
        const body = {
            preferences: this.preferences
        };
        await APIRequest(`obj/user/${this.id}`, {}, {
            method: "PUT",
            body: JSON.stringify(body)
        });
    }
}

export interface InfegyUserStub {
    id: string;
    name: string;
    email: string;
    active: boolean;
}

export interface InfegyAccountStub {
    id: string;
    name: string;
    active: boolean;
}

var _currentUser: InfegyUser | undefined = undefined;
var _sessionWasExpired: boolean = false;
var _heartbeatActive: boolean = false;

async function sessionHeartbeat() {
    if (_heartbeatActive) {
        if (sessionToken()) {
            await APIRequest("heartbeat", undefined, undefined, false, 3, [401]).catch((reason: APIError) => {
                // 401: Unauthorized - Session expired
                if (reason.code === 401 || reason.code === 403) {
                    // Clear the token, mark expired
                    clearExpiredSession();
                }
            });

            // Set up another heartbeat if we still have a session and the heartbeat is still active
            if (sessionToken() && _heartbeatActive) {
                window.setTimeout(sessionHeartbeat, 30 * 1000);
            }
        } else {
            // No token. Any routes needing auth will redirect to login.
        }
    }
}

function setupSessionHeartbeat() {
    if (_heartbeatActive) {
        return;
    }

    // Call the heartbeat every 30 seconds
    _heartbeatActive = true;
    window.setTimeout(sessionHeartbeat, 30 * 1000);
}

// Stop sending heartbeats when the tab goes to the background
document.addEventListener("visibilitychange", () => {
    if (document.hidden) {
        // The tab has gone to the background
      _heartbeatActive = false;
    } else {
        // The tab is brought back to the foreground
        if (_currentUser) {
            setupSessionHeartbeat();
        }
    }
});

export function getInitials(name: string): string {
    if (name) {
        let words = name.split(' ').filter(i => i);
        if (words.length > 1) {
            return (words[0][0] + words[1][0]).toUpperCase();
        } else if (words.length) {
            if (words[0].length > 1) {
                return words[0][0].toUpperCase() + words[0][1];
            } else return words[0][0].toUpperCase();
        }
    }

    return "??";
}

function handleLoginResponse(response) {
    // We expect a bearer token in here
    _currentUser = new InfegyUser(response.user);
    if (!_currentUser.id) {
        throw new APIError('Login got bad user', 401, "login");
    }

    // The token is the combined type and token, like "Bearer [token data]"
    // Save token to local storage
    localStorage.setItem("starscape-auth-token", response.token_type + ' ' + response.access_token);

    // Start the session heartbeat to keep this session alive while the browser is open
    setupSessionHeartbeat();
}

export async function loginCurrentUser(email: string, password: string) {
    // Remove any existing token before logging in
    localStorage.removeItem("starscape-auth-token");
    _heartbeatActive = false;
    _sessionWasExpired = false;

    return APIRequest("login", "", {
        method: "POST",
        body: JSON.stringify({email: email, password: password})
    }, false, 3, [403]).then((response) => {
        if(response.error === "second-factor-required") {
            // We need a 2nd factor
            // The response has info on this
            // we need the login form to present an input for the user to enter the code
            throw response;
        } else {
            // The login succeeded!
            handleLoginResponse(response);
            return true;
        }
    }, (reason) => {
        // The login failed; just re-throw this error for whomever tried to login
        throw reason;
    });
}

export async function loginByTwoFactor(token: string, code: string) {
    // Remove any existing token before logging in
    localStorage.removeItem("starscape-auth-token");
    _heartbeatActive = false;
    _sessionWasExpired = false;

    return APIRequest("login-2fa", "", {
        method: "POST",
        body: JSON.stringify({token: token, code: code})
    }).then((response) => {
        // The login succeeded!
        handleLoginResponse(response);
        return true;
    }, (reason) => {
        // The login failed; just re-throw this error for whomever tried to login
        throw reason;
    });
}

export async function loginBySSO(id_token: string, provider: string) {
  // Remove any existing token before logging in
  localStorage.removeItem("starscape-auth-token");
  _heartbeatActive = false;
  _sessionWasExpired = false;
  
    return APIRequest(`sso/${provider}`, "", {
        method: "POST",
        body: id_token
    }).then((response) => {
      if(response.error === "second-factor-required") {
          // We need a 2nd factor
          // The response has info on this
          // we need the login form to present an input for the user to enter the code
          throw response;
      } else {
          // The login succeeded!
          handleLoginResponse(response);
          return true;
      }
    }, (reason) => {
        // The login failed; just re-throw this error for whomever tried to login
        throw reason;
    });
}

export async function loginByCachedSession() {
    // Look up a cached session token
    if (sessionToken() && sessionToken().length > 0) {
        return APIRequest("me", undefined, undefined, false, 3, [401]).then((response) => {
            // The session re-use succeeded!
            // We can populate user info
            _currentUser = new InfegyUser(response);
            _sessionWasExpired = false;

            // Start the session heartbeat to keep this session alive while the browser is open
            setupSessionHeartbeat();

            return true;
        }, (reason: APIError) => {
            // The token is probably expired; remove it
            localStorage.removeItem("starscape-auth-token");
            _sessionWasExpired = true;
            throw reason;
        });
    }

    return Promise.reject(new Error("No cached session"));
}

export async function logoutCurrentUser() {
    if (sessionToken()) {
        _heartbeatActive = false;   // Immediately disable heartbeat while logout is in-flight
        return await APIRequest("logout").then((response) => {
            _currentUser = undefined;
            return true;
        }).finally(() => {
            // Success or fail, clear the token
            clearSessionToken();
        });
    }
    return Promise.reject(new Error('Not logged in'));
}

// This will remove any active tokens, as well as the localStorage cache
// Use with caution!
export function clearSessionToken() {
    _heartbeatActive = false;
    _sessionWasExpired = false;
    _currentUser = undefined;
    localStorage.removeItem("starscape-auth-token");
}
export function clearExpiredSession() {
    _heartbeatActive = false;
    _sessionWasExpired = localStorage.getItem("starscape-auth-token") ? true : false;
    _currentUser = undefined;
    localStorage.removeItem("starscape-auth-token");
}

export function sessionToken()  {
    return localStorage.getItem("starscape-auth-token") ?? "";
}

export function updateSessionToken(token: string) {
    localStorage.setItem("starscape-auth-token", token);
}

export function currentUser(): InfegyUser | undefined {
    return _currentUser;
}

export function setCurrentUser(value: InfegyUser) {
    _currentUser = value;
}

export function updateCurrentUser(current: InfegyUser, properties: object): void {
    // If we have an existing InfegyUser it will also have an InfegyAccount initialized.  The 
    // problem here is that the constructor expects dates to be in a string format.  In order to 
    // create a new InfegyUser the Date objects in the InfegyUser and InfegyAccount need to be 
    // converted back to strings.
    const updatedAccount = {
        ...current.account,
        created_at: current.account.created_at.toISOString(),
        updated_at: current.account.updated_at.toISOString()
    };
    const updatedUser = new InfegyUser({
        ...current,
        account: updatedAccount,
        created_at: current.created_at.toISOString(),
        updated_at: current.updated_at.toISOString(),
        ...properties
    });
    _currentUser = updatedUser;
}

export function wasSessionExpired() {
    return _sessionWasExpired;
}

export function permissionTest(permission: string) {
    if(!_currentUser)
            return false;
    return _currentUser?.permissions.findIndex((x) => x === permission) !== -1;
}

export function permissionsTest(permissions?: Array<string>) {
    if(!permissions)
        return true;

    if(!_currentUser)
            return false;
    
    for(const permission of permissions) {
        if(_currentUser?.permissions.findIndex((x) => x === permission) === -1)
            return false;
    }
    return true;
}
